import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiAlertCircle, FiCheckCircle, FiClock, FiCreditCard, FiRefreshCw, FiXCircle } from 'react-icons/fi';
import { useMembership } from '../../contexts/MembershipContext';
import membershipApi from '../../services/membershipApi';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return String(value);
  }
};

const formatCurrency = (amountInCents, currency = 'usd') => {
  if (amountInCents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase()
    }).format(amountInCents / 100);
  } catch (e) {
    return `$${(amountInCents / 100).toFixed(2)}`;
  }
};

const statusBadge = (statusValue) => {
  const v = String(statusValue || '').toLowerCase();
  if (v === 'paid' || v === 'succeeded' || v === 'active') {
    return 'bg-green-100 text-green-800';
  }
  if (v === 'past_due' || v === 'open') {
    return 'bg-amber-100 text-amber-800';
  }
  if (v === 'canceled' || v === 'failed') {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-800';
};

const MembershipManagement = () => {
  const { currentMembership, cancelSubscription, resumeSubscription, refreshMembership } = useMembership();
  const [activeTab, setActiveTab] = useState('payments');
  const [history, setHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false);

  const tier = (currentMembership?.type || 'community').toLowerCase();
  const isPaid = tier === 'gold' || tier === 'platinum';
  const hasActiveSubscription = Boolean(currentMembership?.hasActiveSubscription);
  const cancelAtPeriodEnd = Boolean(currentMembership?.cancelAtPeriodEnd);
  const currentPeriodEnd = currentMembership?.currentPeriodEnd;
  const billingInterval = currentMembership?.billingInterval;
  const subscriptionStatus = currentMembership?.subscriptionStatus;

  const loadPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const res = await membershipApi.getPaymentHistory();
      setPayments(res?.data?.payments || []);
    } catch (e) {
      console.error('Failed to load payment history:', e);
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await membershipApi.getMembershipHistory();
      setHistory(res?.data?.history || []);
    } catch (e) {
      console.error('Failed to load membership history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
    loadHistory();
  }, [loadPayments, loadHistory]);

  const handleCancel = async () => {
    if (!window.confirm(
      'Cancel your membership? You will keep your benefits and credits until the end of the current billing period.'
    )) {
      return;
    }
    setActionInFlight(true);
    try {
      const result = await cancelSubscription();
      if (result.success) {
        toast.success(result.data?.message || 'Membership scheduled for cancellation.');
        await Promise.all([loadHistory(), loadPayments()]);
      } else {
        toast.error(result.error?.response?.data?.detail || 'Failed to cancel membership.');
      }
    } finally {
      setActionInFlight(false);
    }
  };

  const handleResume = async () => {
    setActionInFlight(true);
    try {
      const result = await resumeSubscription();
      if (result.success) {
        toast.success(result.data?.message || 'Membership resumed.');
        await refreshMembership();
      } else {
        toast.error(result.error?.response?.data?.detail || 'Failed to resume membership.');
      }
    } finally {
      setActionInFlight(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <FiCreditCard className="h-6 w-6 text-primary-600 mt-1" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Your Membership</h2>
            <p className="text-sm text-gray-600 mt-1">
              Current plan:{' '}
              <span className="font-semibold capitalize">{tier}</span>
              {isPaid && billingInterval ? (
                <>
                  {' '}
                  <span className="text-gray-500">·</span>{' '}
                  <span className="capitalize">{billingInterval}</span> billing
                </>
              ) : null}
            </p>
            {isPaid && hasActiveSubscription && (
              <p className="text-sm text-gray-600 mt-1">
                {cancelAtPeriodEnd ? (
                  <>Access ends on <span className="font-semibold">{formatDate(currentPeriodEnd)}</span></>
                ) : (
                  <>Renews on <span className="font-semibold">{formatDate(currentPeriodEnd)}</span></>
                )}
              </p>
            )}
            {subscriptionStatus && subscriptionStatus !== 'active' && (
              <p className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                <FiAlertCircle className="h-3 w-3" /> Subscription status: {subscriptionStatus}
              </p>
            )}
          </div>
        </div>

        {isPaid && hasActiveSubscription && (
          <div className="flex gap-2">
            {cancelAtPeriodEnd ? (
              <button
                onClick={handleResume}
                disabled={actionInFlight}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
              >
                <FiRefreshCw className="h-4 w-4" /> Resume Membership
              </button>
            ) : (
              <button
                onClick={handleCancel}
                disabled={actionInFlight}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
              >
                <FiXCircle className="h-4 w-4" /> Cancel Membership
              </button>
            )}
          </div>
        )}
      </div>

      {isPaid && hasActiveSubscription && cancelAtPeriodEnd && (
        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
          <FiClock className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            Your membership will end on <strong>{formatDate(currentPeriodEnd)}</strong>.
            You'll keep your current benefits and unused credits until then. Click
            "Resume Membership" anytime before to keep your plan active.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            className={`pb-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'payments'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Payment History
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`pb-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'history'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Membership History
          </button>
        </nav>
      </div>

      {activeTab === 'payments' && (
        <div className="mt-4">
          {loadingPayments ? (
            <div className="text-sm text-gray-500 py-6 text-center">Loading payments…</div>
          ) : payments.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">
              No membership payments yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Period</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.map((p) => (
                    <tr key={p.id || p.stripeInvoiceId}>
                      <td className="py-2 pr-4 whitespace-nowrap">{formatDate(p.createdAt)}</td>
                      <td className="py-2 pr-4 capitalize">
                        {p.tier || '—'}
                        {p.billingInterval ? (
                          <span className="text-gray-500"> · {p.billingInterval}</span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-600">
                        {formatDate(p.periodStart)} → {formatDate(p.periodEnd)}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatCurrency(p.amountPaid, p.currency)}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusBadge(p.status)}`}>
                          {p.status || 'unknown'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {p.invoiceHostedUrl ? (
                          <a
                            href={p.invoiceHostedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="mt-4">
          {loadingHistory ? (
            <div className="text-sm text-gray-500 py-6 text-center">Loading history…</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">
              No membership history yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-4">Tier</th>
                    <th className="py-2 pr-4">Billing</th>
                    <th className="py-2 pr-4">Started</th>
                    <th className="py-2 pr-4">Ended</th>
                    <th className="py-2 pr-4">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="py-2 pr-4 capitalize font-medium">
                        {h.type}
                        {!h.endDate && (
                          <FiCheckCircle className="inline ml-1 h-3 w-3 text-green-500" title="Active" />
                        )}
                      </td>
                      <td className="py-2 pr-4 capitalize text-gray-600">
                        {h.billingInterval || '—'}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">{formatDate(h.startDate)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {h.endDate ? formatDate(h.endDate) : <span className="text-green-600">Active</span>}
                      </td>
                      <td className="py-2 pr-4 text-gray-500">
                        {h.cancellationReason || h.downgradeReason || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MembershipManagement;
